import type {
  AnalyticsSetupDraft,
  AnyCampaignDraft,
  CampaignDraft,
  CampaignPlan,
  Deliverable,
} from './api.js';

// Legacy adapter (s181, extended s182).
//
// New campaign drafts emit the free-form CampaignPlan shape. Legacy
// campaigns on the DB carry either the rigid creative-ads CampaignDraft
// (6 hooks, fixed audience/budget/oneAsk fields) or the rigid
// AnalyticsSetupDraft (events + prerequisites + postInstallCheck). The
// dashboard treats the plan as the canonical render shape; this
// adapter promotes both legacy variants into the same view so one set
// of UI components renders all three.
//
// Note: as of s182 the analytics-setup AGENT still emits the rigid
// AnalyticsSetupDraft on the wire — the adapter converts on read.
// A future session can rewrite the analytics-setup agent to emit a
// free-form plan directly (with optional non-analytics deliverables
// alongside the events). The dashboard side won't need to change.

export function draftToPlanView(draft: AnyCampaignDraft): CampaignPlan {
  if ((draft as { shape?: unknown }).shape === 'plan') {
    return draft as CampaignPlan;
  }
  if ((draft as { kind?: unknown }).kind === 'analytics_setup') {
    return legacyAnalyticsSetupToPlan(draft as AnalyticsSetupDraft);
  }
  // The legacy creative_ads shape is the only remaining draft this
  // adapter knows how to render — it requires a `creative` array.
  // organic_meta (MetaContentPlanOutput, `slots[]`) has its own
  // dedicated surface and is redirected away before reaching here;
  // any other draft shape lands here as a bug, not a silent crash on
  // `.creative.map`. Throw with a hint so the next mystery shape
  // surfaces clearly in the error boundary.
  if (!Array.isArray((draft as { creative?: unknown }).creative)) {
    throw new Error(
      `draftToPlanView: unsupported draft shape for review — expected a CampaignPlan, AnalyticsSetupDraft, or legacy creative_ads CampaignDraft. ` +
        `Got keys: ${Object.keys(draft as object).join(', ')}.`,
    );
  }
  return legacyCreativeAdsToPlan(draft as CampaignDraft);
}

function legacyAnalyticsSetupToPlan(draft: AnalyticsSetupDraft): CampaignPlan {
  // Each analytics event becomes one analytics_event deliverable.
  // The event id + name + when + snippet + snippetKind map 1:1; the
  // legacy `why` field carries through as the deliverable's `why`.
  const deliverables: Deliverable[] = draft.events.map((e) => ({
    kind: 'analytics_event' as const,
    id: e.id,
    name: e.name,
    why: e.why,
    when: e.when,
    snippet: e.snippet,
    snippetKind: e.snippetKind,
  }));
  return {
    shape: 'plan',
    name: draft.name,
    pitch: draft.pitch,
    // analytics_setup drafts persisted a free-text goal (s125+);
    // older rows leave it null, in which case we fall back to the
    // postInstallCheck promise — same fallback the campaign-detail
    // page used to do inline.
    goal: draft.goal ?? draft.postInstallCheck,
    deliverables,
    optimizingFor: draft.goal ?? draft.postInstallCheck,
    // Analytics setups don't carry a timeline on the legacy schema —
    // surface a generic in mag voice. Real plans set this explicitly.
    timeline: "live as soon as the snippets are in. i'll start watching the day after.",
    honestConcerns: [],
    // Lift the campaign-level analytics-setup fields onto the plan.
    prerequisites: draft.prerequisites,
    postInstallCheck: draft.postInstallCheck,
  };
}

function legacyCreativeAdsToPlan(draft: CampaignDraft): CampaignPlan {
  // Map each legacy creative entry to a paid_ad_hook deliverable.
  // Legacy creatives had no `why` field; we synthesize a generic one
  // so the new tile UI still renders something sensible. Once a
  // legacy campaign is re-drafted (post-s181), it'll get real
  // per-hook justifications.
  //
  // s183 — paid_ad_hook now requires a stable id (so publish + other
  // per-deliverable actions can target a specific row). Legacy rows
  // never had ids; we synthesize predictable ones from their
  // position so two renders of the same legacy draft pick the same
  // id and any "iterate this hook" UI doesn't lose track.
  const deliverables: Deliverable[] = draft.creative.map((c, i) => ({
    kind: 'paid_ad_hook' as const,
    id: `legacy-hook-${i + 1}`,
    hook: c.hook,
    bg: c.bg,
    why: '(legacy draft — pre-s181, no per-hook reasoning was captured)',
  }));
  return {
    shape: 'plan',
    name: draft.name,
    pitch: draft.pitch,
    // Legacy drafts didn't have a freestanding goal field — derive
    // from optimizingFor so the plan view still has one. New drafts
    // emit a richer measurable goal.
    goal: draft.optimizingFor,
    deliverables,
    audience: draft.audience,
    budget: draft.budget,
    optimizingFor: draft.optimizingFor,
    timeline: draft.timeline,
    // Legacy drafts pre-date honest-concerns; surface an empty list
    // so the UI renders the "↘ mag's caveats" block as "none flagged
    // — this draft was made before mag started flagging her own
    // uncertainty." (UI handles the empty case.)
    honestConcerns: [],
    // Creative-ads drafts didn't carry these; default to empty so
    // the plan-level blocks (PrerequisitesBlock, postInstallCheck)
    // render their no-op state.
    prerequisites: [],
    oneAsk: draft.oneAsk,
  };
}
